import React from "react";
import { createPortal } from "react-dom";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useFocusTrap, useUniqueId } from "../../utils/accessibility";
import Button from "./Button";

type ModalSize = "sm" | "md" | "lg" | "xl" | "2xl" | "full";

const SIZE_CLASSES: Record<ModalSize, string> = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-2xl",
  xl: "max-w-3xl",
  "2xl": "max-w-4xl",
  full: "max-w-6xl",
};

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Header title. Pass a string for the standard heading, or a node for a custom header. */
  title?: React.ReactNode;
  /** The dialog's accessible name when it has no title, e.g. a header-less announcement. */
  ariaLabel?: string;
  children: React.ReactNode;
  /** Pinned footer region (e.g. action buttons). */
  footer?: React.ReactNode;
  size?: ModalSize;
  /** Close when the backdrop is clicked. Default true. */
  closeOnBackdrop?: boolean;
  /** Close when Escape is pressed. Default true. Set false when a nested dialog owns Escape. */
  closeOnEscape?: boolean;
  /** Hide the header close (X) button. Default false. */
  hideCloseButton?: boolean;
  /** Extra classes for the panel element. */
  className?: string;
  /** Overrides the default body padding (`px-6 py-5`) — pass e.g. "p-0" for full-bleed content. */
  bodyClassName?: string;
  /** Overrides the footer layout classes (default `flex items-center justify-end gap-3`). */
  footerClassName?: string;
}

/**
 * The shared modal/dialog shell. Standardizes the overlay, centered panel, portal
 * rendering, focus trapping, Escape-to-close, and backdrop click-to-close so no page
 * hand-rolls `fixed inset-0` overlays. Use `ConfirmModal` for simple confirm/cancel prompts.
 */
const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  ariaLabel,
  children,
  footer,
  size = "md",
  closeOnBackdrop = true,
  closeOnEscape = true,
  hideCloseButton = false,
  className = "",
  bodyClassName,
  footerClassName = "flex items-center justify-end gap-3",
}) => {
  const containerRef = useFocusTrap<HTMLDivElement>(isOpen, closeOnEscape ? onClose : undefined);
  const titleId = useUniqueId("modal-title");
  // Only treat a backdrop click as a dismissal when the press also started on the
  // backdrop — otherwise selecting text in the panel and releasing outside closes it.
  const mouseDownOnBackdrop = React.useRef(false);

  if (!isOpen) return null;

  const hasHeader = title !== undefined || !hideCloseButton;

  const modal = (
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/40 px-4 py-6"
      onMouseDown={(e) => {
        mouseDownOnBackdrop.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        if (closeOnBackdrop && mouseDownOnBackdrop.current && e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title !== undefined ? titleId : undefined}
        aria-label={title === undefined ? ariaLabel : undefined}
        className={`relative bg-white rounded-xl shadow-lg w-full ${SIZE_CLASSES[size]} flex flex-col max-h-[85vh] animate-slide-in-center ${className}`}
        onClick={(e) => e.stopPropagation()}
      >
        {hasHeader && (
          <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-gray-100 flex-shrink-0">
            {title !== undefined ? (
              typeof title === "string" ? (
                <h2 id={titleId} className="text-base font-bold text-gray-900">
                  {title}
                </h2>
              ) : (
                <div id={titleId} className="min-w-0 flex-1">
                  {title}
                </div>
              )
            ) : (
              <span aria-hidden="true" />
            )}
            {!hideCloseButton && (
              <Button
                variant="ghost"
                size="sm"
                type="button"
                onClick={onClose}
                aria-label="Close dialog"
                className="flex-shrink-0"
                icon={<XMarkIcon className="w-5 h-5" />}
              />
            )}
          </div>
        )}

        <div className={`flex-1 min-h-0 overflow-y-auto ${bodyClassName ?? "px-6 py-5"}`}>
          {children}
        </div>

        {footer && (
          <div className={`${footerClassName} px-6 py-4 border-t border-gray-100 flex-shrink-0`}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  return typeof document !== "undefined" ? createPortal(modal, document.body) : null;
};

export default Modal;
